import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../utils/cn';

const GoogleAppsLauncher = ({ isOpen, onClose }) => {
  const launcherRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (launcherRef.current && !launcherRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const googleApps = [
    // Row 1
    {
      name: 'Account',
      url: 'https://myaccount.google.com',
      icon: 'https://ssl.gstatic.com/gb/images/p1_8b8b8b8b.png'
    },
    {
      name: 'Drive',
      url: 'https://drive.google.com',
      icon: 'https://ssl.gstatic.com/docs/documents/images/kix-favicon7.ico'
    },
    {
      name: 'Business Profile',
      url: 'https://business.google.com',
      icon: 'https://ssl.gstatic.com/gb/images/p1_8b8b8b8b.png'
    },
    // Row 2
    {
      name: 'Gmail',
      url: 'https://mail.google.com',
      icon: 'https://ssl.gstatic.com/ui/v1/icons/mail/rfr/gmail.ico'
    },
    {
      name: 'YouTube',
      url: 'https://youtube.com',
      icon: 'https://www.youtube.com/s/desktop/favicon.ico'
    },
    {
      name: 'Gemini',
      url: 'https://gemini.google.com',
      icon: 'https://ssl.gstatic.com/gb/images/p1_8b8b8b8b.png'
    },
    // Row 3
    {
      name: 'Maps',
      url: 'https://maps.google.com',
      icon: 'https://maps.gstatic.com/mapfiles/api-3/images/icon_red.png'
    },
    {
      name: 'Search',
      url: 'https://google.com',
      icon: 'https://www.google.com/favicon.ico'
    },
    {
      name: 'Calendar',
      url: 'https://calendar.google.com',
      icon: 'https://calendar.google.com/googlecalendar/images/favicon_2020q4_2.ico'
    },
    // Row 4
    {
      name: 'News',
      url: 'https://news.google.com',
      icon: 'https://ssl.gstatic.com/gb/images/p1_8b8b8b8b.png'
    },
    {
      name: 'Photos',
      url: 'https://photos.google.com',
      icon: 'https://ssl.gstatic.com/gb/images/p1_8b8b8b8b.png'
    },
    {
      name: 'Meet',
      url: 'https://meet.google.com',
      icon: 'https://ssl.gstatic.com/gb/images/p1_8b8b8b8b.png'
    },
    // Row 5 (additional apps)
    {
      name: 'Sheets',
      url: 'https://sheets.google.com',
      icon: 'https://ssl.gstatic.com/docs/documents/images/kix-favicon7.ico'
    },
    {
      name: 'Translate',
      url: 'https://translate.google.com',
      icon: 'https://ssl.gstatic.com/gb/images/p1_8b8b8b8b.png'
    },
    {
      name: 'Docs',
      url: 'https://docs.google.com',
      icon: 'https://ssl.gstatic.com/docs/documents/images/kix-favicon7.ico'
    },
    {
      name: 'Slides',
      url: 'https://slides.google.com',
      icon: 'https://ssl.gstatic.com/docs/documents/images/kix-favicon7.ico'
    },
    {
      name: 'Google One',
      url: 'https://one.google.com',
      icon: 'https://ssl.gstatic.com/gb/images/p1_8b8b8b8b.png'
    }
  ];

  const handleAppClick = (url) => {
    window.open(url, '_blank');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-end pt-20 pr-4">
      <div
        ref={launcherRef}
        className="bg-zinc-800 border border-zinc-700 rounded-xl shadow-2xl p-4 max-w-sm max-h-[500px]"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-medium text-sm">Google Apps</h3>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Apps Grid */}
        <div className="grid grid-cols-3 gap-3 max-h-96 overflow-y-auto">
          {googleApps.map((app, index) => (
            <button
              key={index}
              onClick={() => handleAppClick(app.url)}
              className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-zinc-700 transition-colors group"
            >
              <img
                src={app.icon}
                alt={`${app.name} icon`}
                className="w-8 h-8 rounded"
                onError={(e) => {
                  // Fallback to a default icon if the Google icon fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div 
                className="w-8 h-8 bg-zinc-600 rounded items-center justify-center"
                style={{ display: 'none' }}
              >
                <span className="text-white text-xs font-bold">
                  {app.name.charAt(0)}
                </span>
              </div>
              <span className="text-xs text-zinc-300 group-hover:text-white transition-colors text-center">
                {app.name}
              </span>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-3 border-t border-zinc-700">
          <button
            onClick={() => handleAppClick('https://myaccount.google.com')}
            className="w-full text-center text-xs text-zinc-400 hover:text-white transition-colors"
          >
            Manage your Google Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoogleAppsLauncher;
